import {
  isEditableElementFocused,
  isModalDialogOpen,
} from '@src/services/shortcuts/keyboard';
import { useEffect } from 'react';

/**
 * PLAT-11618 — Space toggles playback for the player the calling surface owns.
 *
 * Space is the universal play/pause key, and a surface that mounts a player
 * without claiming it leaves the browser's default in place: the page scrolls
 * instead. The toggle runs on KEYUP with the keydown suppressed, so the scroll
 * never happens and a held key acts once rather than repeating.
 *
 * Typing always wins — the shortcut stands down whenever an editable element
 * has focus. By default it also stands down under a `common/Modal` backdrop: a
 * key that plays a video the user cannot see reads as the app acting behind
 * their back. A surface that IS the backdrop (the translation review
 * workspace) passes `ownsKeyUnderBackdrop` so that check does not stand it
 * down against itself — exactly one player answers the key either way.
 *
 * `onToggle` is called on every qualifying press, so a host that can be
 * mid-load should no-op it until its player reports ready. A host that stays
 * MOUNTED while off screen (the Content Studio tabs are kept mounted to avoid
 * a slow remount) must pass `enabled`, or it claims the key from whatever the
 * user is actually looking at.
 */
export function usePlayerSpaceShortcut(
  onToggle: () => void,
  options: { ownsKeyUnderBackdrop?: boolean; enabled?: boolean } = {},
): void {
  const { ownsKeyUnderBackdrop = false, enabled = true } = options;

  useEffect(() => {
    if (!enabled) return;

    const standDown = () =>
      isEditableElementFocused() ||
      (!ownsKeyUnderBackdrop && isModalDialogOpen());

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'Space' || standDown()) return;
      // Stop the browser's Space page-scroll; the toggle runs on keyup.
      event.preventDefault();
      event.stopPropagation();
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code !== 'Space' || standDown()) return;
      event.preventDefault();
      event.stopPropagation();
      onToggle();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [onToggle, ownsKeyUnderBackdrop, enabled]);
}
